/**
 * =============================================================================
 * Hook: useAffiliateHomeOverview
 * Versão: 2.0.0 (Suporte a filtro de período)
 *
 * Descrição:
 *   Fonte ÚNICA de dados da Home `/afiliado`. Faz uma única chamada à RPC
 *   `get_affiliate_home_overview(p_period_start, p_period_end)` e devolve o
 *   JSON consolidado:
 *     - identidade do afiliado (mascarada)
 *     - saldo (calculado em tempo real do ledger, NUNCA de coluna agregada)
 *     - funil + progresso filtrados pelo período
 *     - quebra por programa
 *     - link primário para compartilhamento
 *     - últimas indicações (com leads mascarados)
 *     - resumo de notificações
 *
 *   Mudança v2.0.0:
 *     - Aceita opcionalmente `{ startDate, endDate }` para filtrar funil,
 *       progresso e programas pelo período selecionado pelo afiliado.
 *     - Sem args, mantém comportamento original (mês corrente em SP).
 *
 * Segurança:
 *   - O `affiliate_id` é resolvido server-side via auth.uid().
 *   - Frontend NÃO envia ID nem tenant.
 *   - Saldos NUNCA vêm de colunas agregadas — sempre recalculados.
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { createLogger } from '@/lib/logger';

const log = createLogger('useAffiliateHomeOverview');

// -----------------------------------------------------------------------------
// Tipos do payload retornado pela RPC
// -----------------------------------------------------------------------------
export interface HomeOverviewAffiliate {
  id_short: string;
  full_name: string;
  avatar_url: string | null;
  email_masked: string;
}

export interface HomeOverviewBalance {
  available: number;
  pending_payout: number;
  total_earned: number;
  total_paid: number;
  in_transit: number;
  min_payout_amount: number;
}

export interface HomeOverviewFunnel {
  clicks: number;
  referrals: number;
  conversions: number;
  conversion_rate: number;
}

export interface HomeOverviewMonthly {
  month_start: string;
  month_end?: string;
  referrals: number;
  conversions: number;
  earned: number;
  trend_pct: number | null;
}

export interface HomeOverviewProgram {
  program_id: string;
  program_id_short: string;
  program_name: string;
  status: string;
  min_payout_amount: number;
  available: number;
  earned: number;
  paid: number;
  /** v2.1.2: saques em pending/processing — exibido como "Em processamento". */
  in_transit: number;
  clicks: number;
  referrals: number;
  conversions: number;
}

export interface HomeOverviewPrimaryLink {
  campaign_id_short: string;
  campaign_name: string;
  short_code: string;
  slug: string;
}

export interface HomeOverviewRecentReferral {
  id_short: string;
  lead_name: string | null;
  lead_email_masked: string | null;
  lead_phone_masked: string | null;
  status: string;
  campaign_name: string | null;
  created_at_iso: string;
}

export interface HomeOverviewNotifications {
  unread_count: number;
  latest_at: string | null;
}

export interface AffiliateHomeOverview {
  affiliate: HomeOverviewAffiliate;
  balance: HomeOverviewBalance;
  funnel_lifetime: HomeOverviewFunnel;
  monthly: HomeOverviewMonthly;
  programs: HomeOverviewProgram[];
  primary_link: HomeOverviewPrimaryLink | null;
  recent_referrals: HomeOverviewRecentReferral[];
  notifications: HomeOverviewNotifications;
  period?: { start: string; end: string; is_custom: boolean };
}

/** Parâmetros opcionais do hook. */
export interface UseAffiliateHomeOverviewParams {
  /** Início do período (inclusivo). Quando undefined → mês corrente. */
  startDate?: Date;
  /** Fim do período (exclusivo). */
  endDate?: Date;
}

/**
 * Carrega a visão consolidada da Home do afiliado autenticado.
 * Usa o `affiliate.id` apenas como chave de cache — o servidor resolve
 * o afiliado real via auth.uid().
 */
export function useAffiliateHomeOverview(
  params: UseAffiliateHomeOverviewParams = {},
) {
  const { affiliate, ready } = useAffiliateAuth();
  const { startDate, endDate } = params;

  // Chaves estáveis para o cache (ISO string ou null)
  const startKey = startDate ? startDate.toISOString() : null;
  const endKey = endDate ? endDate.toISOString() : null;

  return useQuery({
    queryKey: ['affiliate-home-overview', affiliate?.id, startKey, endKey],
    enabled: ready && !!affiliate?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<AffiliateHomeOverview> => {
      const { data, error } = await supabase.rpc('get_affiliate_home_overview', {
        p_period_start: startKey,
        p_period_end: endKey,
      } as any);
      if (error) {
        log.error('Falha ao carregar overview da Home', error);
        throw error;
      }
      return data as unknown as AffiliateHomeOverview;
    },
  });
}
